import qs from "qs";

import { GetDataWithErrors } from "./Functions/SettingData";

// API module for Asset Management -> Work Orders tab.
//
// GetDataWithErrors is not optional here. createWorkOrder's whole contract is a
// STRUCTURED 422 -- {summary, serials[]} with a verdict per serial -- which the
// form renders as a table so the maker can fix and resubmit. GetData would keep
// only responseStatus.msg and throw the table away, and there is deliberately
// no pre-flight validation endpoint to fall back on.

// Params shared by the list, the counts and the export, built in ONE place so a
// download cannot narrow differently from the table it was started from.
// One multi-select filter, encoded for the transport that is about to carry it.
//
// workOrderTypeCode takes one OR many values under the same param name
// (CommaSeparatedStrList, capped at 200), so the mapping hands it down as an
// array and the choice of encoding is made here, once: a query string gets
// `workOrderTypeCode=dispatch,receipt` -- NOT a repeated key, which
// request.args.to_dict() would flatten to the first value alone -- and a POST
// body gets the real array.
//
// An empty selection sends NOTHING, rather than an IN () that reads to a user
// as "this filter found no rows".
const encodeFilterValues = (value, asQueryString) => {
    const values = (Array.isArray(value) ? value : [value]).filter(
        (item) => item !== null && item !== undefined && item !== ""
    );
    if (!values.length) return undefined;
    return asQueryString ? values.join(",") : values;
};

export const buildWorkOrderParams = (
    {
        workOrderTypeCode,
        status,
        createdByMe,
        search,
        searchBy,
        sortBy,
        sortOrder,
    },
    // The list and the counts serialise to a query string; list-action posts a
    // JSON body and passes false.
    { asQueryString = true } = {}
) => {
    const params = {};
    const workOrderTypeCodes = encodeFilterValues(workOrderTypeCode, asQueryString);
    if (workOrderTypeCodes !== undefined) params.workOrderTypeCode = workOrderTypeCodes;
    // status is passed through as the caller wrote it, one value or several
    // comma-joined: the schema parses it with CommaSeparatedStrList and the
    // query layer turns it into WorkOrder.status.in_(...). History sends
    // "approved,rejected" so one paged, server-sorted response carries both.
    if (status) params.status = status;
    if (createdByMe) params.createdByMe = true;
    if (search) params.search = search;
    if (searchBy) params.searchBy = searchBy;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    return params;
};

// GET /api/work-orders/config
//
// `types` is optional and defaults to BOTH tables. The queue passes
// ["workOrderType"] so it never pulls movementType's two JSON columns for a
// dropdown it does not render; the New Work Order form needs both.
// Response is keyed by table name, and carries only the keys asked for.
export const getWorkOrderConfig = async (types) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/work-orders/config${
            types?.length ? `?${qs.stringify({ types: types.join(",") })}` : ""
        }`,
    });

// GET /api/work-orders
export const getWorkOrders = async ({ page = 1, limit = 25, ...rest }) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/work-orders?${qs.stringify({
            ...buildWorkOrderParams(rest),
            page,
            limit,
        })}`,
    });

// GET /api/work-orders/counts -> { pending, approved, rejected }
// Honours the list filters except `status`, so the badges say what the other
// tabs hold. page and limit are accepted and ignored.
export const getWorkOrderCounts = async (args) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/work-orders/counts?${qs.stringify(buildWorkOrderParams(args))}`,
    });

// GET /api/work-orders/<id>
// The whole request in one call: work order, movements, items and resolved
// labels. Nothing is lazy-loaded, so the detail view needs no second request.
export const getWorkOrder = async (workOrderID) =>
    GetDataWithErrors({ method: "get", url: `/api/work-orders/${workOrderID}` });

// GET /api/work-orders/search-suggestions
// columnName is one of workOrderReferenceID, serialNumber, createdByName,
// remarks. Serial suggestions come from assets that actually appear on a live
// work order, so every suggestion leads somewhere.
export const getWorkOrderSearchSuggestions = async (columnName, columnSearch, columnLimit = 10) => {
    const res = await GetDataWithErrors({
        method: "get",
        url: `/api/work-orders/search-suggestions?${qs.stringify({
            columnName,
            columnSearch,
            columnLimit,
        })}`,
    });
    const values = Array.isArray(res?.data) ? res.data : [];
    return {
        responseStatus: res?.responseStatus || { code: 0, msg: null },
        response: { data: values },
    };
};

// POST /api/work-orders/list-action -- download-csv | mail.
//
// Sends NO `rows`: the server builds the file from the same query and the same
// column selection, and actionType decides only whether it is also mailed, so
// the download and the mail cannot disagree. The schema is unknown=RAISE, so
// sending `rows` is a 400 rather than an ignored field.
export const workOrderListAction = async ({
    actionType,
    columns,
    columnOrder,
    sendTo,
    sendCc,
    subject,
    body,
    fileName,
    ...filters
}) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/work-orders/list-action",
        headers: { "Content-Type": "application/json" },
        data: {
            actionType,
            columns,
            columnOrder,
            ...(actionType === "mail"
                ? { sendTo, sendCc: sendCc || [], subject, body, fileName }
                : {}),
            ...buildWorkOrderParams(filters, { asQueryString: false }),
        },
    });

// POST /api/work-orders -- the only write path into a work order.
//
// payload: { workOrderTypeCode, remarks, movements: [ { movementTypeCode,
//   movementDateTime, destinationEntityID, completesRecordID,
//   items: [ { serialNumber, sourceEntityID, returnReasonCode,
//              replacesAssetID, loanReferenceID, itemData } ] } ] }
//
// Sends no documentIDs: document upload is deferred, and the schema uses
// unknown=RAISE, so sending the key would be an error rather than ignored.
//
// A 422 carries { summary, serials } -- READ IT, do not stringify it. See
// isSerialReport below.
export const createWorkOrder = async (payload) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/work-orders",
        headers: { "Content-Type": "application/json" },
        data: payload,
    });

// POST /api/work-orders with validateOnly -- the DRY RUN.
//
// Same endpoint, same payload, same resolver. The service stops at step 4b,
// after every check and before the first write, and answers 200 with the very
// {summary, serials} body the 422 carries. That is the whole point: the check
// and the create cannot disagree, because they are one function.
//
// TWO SUCCESSFUL SHAPES, and the caller must read both:
//   200 + {summary, serials}  -- every serial resolved, nothing was written
//   422 + {summary, serials}  -- some did not, and the request stopped earlier
// Both are the same table. isSerialReport() detects them by shape, so neither
// branch needs the status code.
//
// The permission guard at step 2 runs BEFORE this, so a user without `create`
// on the type cannot use the dry run to probe which serials exist.
export const checkWorkOrderSerials = async (payload) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/work-orders",
        headers: { "Content-Type": "application/json" },
        data: { ...payload, validateOnly: true },
    });

// PUT /api/work-orders/<id> -- maker only, while still pending.
// Replaces the whole movement set; the previous one is soft-deleted server-side.
export const updateWorkOrder = async (workOrderID, payload) =>
    GetDataWithErrors({
        method: "put",
        url: `/api/work-orders/${workOrderID}`,
        headers: { "Content-Type": "application/json" },
        data: payload,
    });

// DELETE /api/work-orders/<id> -- the maker withdraws their own pending request.
export const deleteWorkOrder = async (workOrderID) =>
    GetDataWithErrors({ method: "delete", url: `/api/work-orders/${workOrderID}` });

// POST /api/work-orders/<id>/review -- the only call in the module that moves
// asset state.
//
// action: "approve" | "reject". reviewRemark rides on BOTH -- the checker's
// note is recorded on an approval as much as on a rejection. The API requires
// it only to reject; the dialog requires it either way.
//
// Approve can still fail with a 409 carrying the SAME {summary, serials} shape
// as the create 422: custody can have moved in the days since the request was
// raised, and approval re-resolves every serial rather than trusting the
// snapshot. Render it with the same component.
export const reviewWorkOrder = async (workOrderID, { action, reviewRemark }) =>
    GetDataWithErrors({
        method: "post",
        url: `/api/work-orders/${workOrderID}/review`,
        headers: { "Content-Type": "application/json" },
        data: { action, ...(reviewRemark ? { reviewRemark } : {}) },
    });

// True when a failed response carries the per-serial verdict table rather than
// a plain message -- a 422 from create, or a 409 from approve.
//
// The guard is the shape, not the status code: both paths produce it, and a
// caller that switches on the code alone will miss one of them.
export const isSerialReport = (res) =>
    Boolean(res?.data?.summary && Array.isArray(res?.data?.serials));
